import { useCallback, useEffect } from 'react';
import { streamAgentChat } from '../../api/agentChat';
import { useAgentChatSession } from '../../api/hooks';
import { useAgentChatStore, EMPTY_SLICE } from '../../store/agentChatStore';
import { useWorkspaceStore } from '../../store/workspaceStore';
import { isCanvasDirective } from '../../types';
import { captureCanvasContext } from './canvasContext';

export function useAgentChat(catalogId: string) {
  const replayQuery = useAgentChatSession(catalogId);
  const slice = useAgentChatStore(
    (s) => s.byCatalog[catalogId] ?? EMPTY_SLICE,
  );

  const beginSend = useAgentChatStore((s) => s.beginSend);
  const appendDelta = useAgentChatStore((s) => s.appendDelta);
  const appendToolStart = useAgentChatStore((s) => s.appendToolStart);
  const resolveTool = useAgentChatStore((s) => s.resolveTool);
  const finishStream = useAgentChatStore((s) => s.finishStream);
  const failStream = useAgentChatStore((s) => s.failStream);
  const consumeReplayUpdate = useAgentChatStore((s) => s.consumeReplayUpdate);
  // The agent navigates the canvas via the BE `open_view` tool (SC-2664), which emits a
  // CanvasDirective over the SSE stream. It's routed through the canvas dispatch hub, which
  // forwards an entities directive's params to `tableViewStore.applyDirective` (so the agent
  // still drives the table, as the old get_table_slice did), applies a findings directive's
  // filters via the URL (SC-2766), and records the canvas view + provenance.
  const applyCanvasDirective = useWorkspaceStore((s) => s.applyCanvasDirective);

  // Drop local-only groups whenever the replay query lands fresh data. With
  // `staleTime: Infinity` + `gcTime: 5 min`, cache eviction after a hiatus
  // triggers a refetch that now includes the previously local turns (BE
  // persisted them). Without this clear, ChatTimeline would render
  // replayGroups + localGroups and duplicate every completed message.
  //
  // The "last consumed updatedAt" lives in the per-catalog slice (not a hook
  // ref) so a plain unmount-then-remount with cached data is a no-op — only
  // a real refetch with a new timestamp drops the local overlay.
  useEffect(() => {
    const updatedAt = replayQuery.dataUpdatedAt;
    if (!updatedAt) return;
    consumeReplayUpdate(catalogId, updatedAt);
  }, [replayQuery.dataUpdatedAt, catalogId, consumeReplayUpdate]);

  const send = useCallback(
    async (content: string) => {
      // Guard against concurrent sends. The UI disables the input/chips while
      // streaming, but a programmatic double-call (or a rapid click before
      // React commits the disabled state) could slip past — without this
      // guard, both fetches would mutate the same inFlight buffer.
      if (useAgentChatStore.getState().byCatalog[catalogId]?.status === 'streaming') {
        return;
      }
      const ctrl = new AbortController();
      beginSend(catalogId, content, ctrl);
      // SC-2853 — snapshot the workspace AT SEND TIME (view + params + open
      // overlay) so the agent knows what the user is looking at. Undefined on
      // any internal failure → the turn is just context-blind, never blocked.
      const canvasContext = captureCanvasContext(catalogId);
      try {
        await streamAgentChat(
          catalogId,
          content,
          {
            onDelta: (text) => appendDelta(catalogId, text),
            onToolStart: (payload) => appendToolStart(catalogId, payload),
            onToolDone: (payload) => {
              resolveTool(catalogId, {
                id: payload.id,
                summary: payload.summary,
                isError: payload.is_error,
                content: payload.content,
              });
              // SC-2663/SC-2664/SC-2766 — the agent-navigation consumer. The BE `open_view`
              // tool emits a full CanvasDirective (retiring get_table_slice: the entities table
              // is now just one branch of it); the result still records in the chat timeline
              // (resolveTool above), and here we additionally dispatch it through the canvas hub.
              // The hub forwards an entities directive's params to `tableViewStore.applyDirective`
              // (the agent still drives the table, byte-identical to the old get_table_slice),
              // applies a findings directive's filters via the URL, and records the canvas view +
              // provenance. A malformed/errored payload fails the `isCanvasDirective` guard and is
              // a no-op. `source` is forced to 'agent' (an open_view result is agent-produced, so a
              // wire glitch can't suppress the provenance banner).
              // Typed `unknown` so the guard narrows cleanly to CanvasDirective — the declared
              // `string | Record | undefined` would leave a `string` intersection that breaks the
              // spread below.
              const openViewContent: unknown = payload.content;
              if (
                payload.tool === 'open_view' &&
                !payload.is_error &&
                isCanvasDirective(openViewContent)
              ) {
                applyCanvasDirective(catalogId, { ...openViewContent, source: 'agent' });
              }
            },
            onDone: () => finishStream(catalogId),
            onError: (message) => failStream(catalogId, message),
          },
          ctrl.signal,
          canvasContext,
        );
      } catch (e) {
        // DOMException is not always an `Error` instance in jsdom; check `.name`
        // directly so abort detection works in both real browsers and tests.
        const name = (e as { name?: unknown } | null)?.name;
        const message =
          name === 'AbortError'
            ? 'Stream aborted'
            : e instanceof Error
              ? e.message
              : 'Network error';
        failStream(catalogId, message);
      }
    },
    [
      catalogId,
      beginSend,
      appendDelta,
      appendToolStart,
      resolveTool,
      finishStream,
      failStream,
      applyCanvasDirective,
    ],
  );

  const abort = useCallback(() => {
    slice.abortController?.abort();
  }, [slice.abortController]);

  return {
    replayQuery,
    localGroups: slice.localGroups,
    pendingUserText: slice.pendingUserText,
    inFlight: slice.inFlight,
    status: slice.status,
    error: slice.error,
    send,
    abort,
  };
}
